import { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { completeAuthRedirect, useSession } from '../lib/session';
import { onwardTo } from '../lib/intent';

/**
 * Full-screen branded loading, and — the part that matters — a failure path.
 * A callback that hangs is the worst thing that can happen at check-in, so if
 * no session has appeared after a few seconds this goes back to the login
 * screen with a sentence rather than sitting on a monogram forever.
 */
export default function AuthCallback() {
  const session = useSession();
  const location = useLocation() as { state?: { from?: string } };
  const [gaveUp, setGaveUp] = useState(false);
  /**
   * Whether the profile has landed, not merely whether a session exists.
   *
   * `completeAuthRedirect` awaits `loadProfile()` on purpose — its own comment
   * says so, and names the symptom: "You're in, Guest" over the fallback flag.
   * That await was being defeated from here. `fromToken` calls `commit()`
   * *before* the round trip, `useSession` re-renders this screen on it, and the
   * redirect below fired on that intermediate session — so /welcome mounted
   * with `name: 'Guest'` and `team: TEAM_FALLBACK` and corrected itself a beat
   * later. The reveal flag flickered from the fallback to the real one on the
   * one screen whose whole job is telling somebody which team is theirs, and
   * the name field, seeded once, never corrected at all.
   *
   * Holding here costs the guest nothing they can see: they are already
   * watching "Signing you in…" through the token exchange, and this is one more
   * request on the same connection.
   */
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const id = setTimeout(() => setGaveUp(true), 6000);
    return () => clearTimeout(id);
  }, []);

  // The PKCE exchange. Managed login sends the browser back here with `?code=`,
  // and this is where it becomes a session (D47). `error=` arrives instead when
  // the guest cancels at Google, which is a normal thing to do, not a fault.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const code = params.get('code');
    // Nothing to exchange: either the guest cancelled at Google, or they are
    // already signed in and landed here. Neither is waiting on a profile.
    if (!code) { if (params.get('error')) setGaveUp(true); else setReady(true); return; }
    let live = true;
    completeAuthRedirect(code)
      .catch(() => { if (live) setGaveUp(true); })
      .finally(() => {
        if (live) setReady(true);
        // Drop `?code=` so a reload does not retry a one-time code and fail.
        window.history.replaceState({}, '', '/auth/callback');
      });
    return () => { live = false; };
  }, []);

  // `gaveUp` releases the hold as well as failing the sign-in, and the order of
  // these two matters. A guest whose token exchange worked but whose `/me` is
  // still hanging on resort wifi is signed in; sending them back to the login
  // screen after six seconds would be a worse outcome than the stale flag this
  // hold exists to prevent, so they go on through with the cached fallback.
  if (session && (ready || gaveUp)) {
    // A guest who has done all this before goes where they were going.
    // `onwardTo` is shared with `Login` so the two cannot drift, and both
    // carriers go through the same allowlist — `state.from` is a second string
    // feeding the same `<Navigate>` and has no more right to be trusted than
    // the stored one. In practice this resolves to the breadcrumb here:
    // managed login is a full page load and `location.state` did not survive
    // it. Somebody who has *not* seen the welcome screen goes there first, and
    // `Welcome` picks the link up on the other side.
    const onward = session.needsWelcome ? '/welcome' : onwardTo(location.state?.from);
    return <Navigate to={onward} replace />;
  }
  if (gaveUp) {
    return <Navigate to="/" replace state={{ authError: true }} />;
  }

  return (
    <div style={{ minHeight: '100dvh', display: 'grid', placeItems: 'center', textAlign: 'center', padding: 32 }}>
      <div>
        <div style={{ fontFamily: 'var(--font-heading)', fontSize: 56 }}>M&amp;V</div>
        <p style={{ fontSize: 13, opacity: 0.6, marginTop: 10 }}>Signing you in…</p>
      </div>
    </div>
  );
}
